import { differenceInSeconds } from 'date-fns'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { parseOptionalISODate } from '@b-stock/fe-core/bstock-react'

import { useProblemFreeOrderRateAnalytics } from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { FeatureFlags } from '@/helpers/featureFlags'

import { useOfferDecisionState } from '../actionCard/offer/useOfferDecisionState'

/**
 * The enrichment every make-an-offer funnel event carries — the offer analog of
 * the auction bid-box bundle. Returns a getter rather than an object so
 * `time_left` reports the click instead of the render.
 *
 * Deliberately omitted: an offer count (offer volume is private) and
 * `experiment_variants` (quick offers aren't behind the quick-bid experiment).
 */
export const useOfferAnalytics = (
  listing: OrchestratedOfferingListingDetails
) => {
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const p2sData = useListingP2SData(listing.lot.locationOriginalId)
  const problemFreeOrderRateAnalytics =
    useProblemFreeOrderRateAnalytics(listing)
  const offerState = useOfferDecisionState(listing)
  const offerEndTime = parseOptionalISODate(listing.offering?.offerEndTime)

  return () => ({
    offer_state: offerState,
    screenName: 'listing_detail_page',
    time_left: offerEndTime
      ? differenceInSeconds(offerEndTime, new Date())
      : null,
    ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
    ...problemFreeOrderRateAnalytics,
  })
}
